import React, { FunctionComponent } from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { Paper, SvgIcon } from "@mui/material";
import { UserRole } from "avvir";

import "../../../css/nav_bar/nav_bar.css";
import "../../../css/nav_bar/nav_bar_redesign.css";
import * as AnalyzeLogo from "../../../resources/logo/analyze-logo.svg";
import getFloorIdFromLocation from "../../services/getters/location_metadata/get_floor_id_from_location";
import getOrganizationId from "../../services/getters/organization_getters/get_organization_id";
import getPageName from "../../services/getters/location_metadata/get_page_name";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import getRedesignPageTitle from "../../services/getters/get_redesign_page_title";
import getScanDatasetIdFromLocation from "../../services/getters/location_metadata/get_scan_dataset_id_from_location";
import getUserIsLoggedIn from "../../services/getters/user_getters/get_user_is_logged_in";
import getUserRole from "../../services/getters/user_getters/get_user_role";
import Link from "../../vendor/redux-first-router-link";
import NavBarRedesignActions from "./redesign/nav_bar_redesign_actions";
import NavBarRedesignDestinations from "./redesign/nav_bar_redesign_destinations";
import PageNames from "../../models/domain/enums/page_names";
import Tooltipable from "../shared/tooltipable";
import toProjectPortfolioPage from "../../events/routing/to_project_portfolio_page";
import toSuperadminOrganizations from "../../events/routing/superadmin/to_superadmin_organizations";
import useRedesignNavGroups from "./redesign/use_redesign_nav_groups";

// The "Analyze logo" exported from the Figma top nav: the tile fills its 32px box. The legacy nav keeps
// hexagon-logo.svg, whose tile sits slightly inside its box.
const LogoIcon = <SvgIcon className="NavBar-logo" component={AnalyzeLogo.ReactComponent} viewBox="0 0 32 32"/>;

export type Props = ReturnType<typeof mapStateToProps>;

// Reskinned top nav. Renders on every page the legacy nav does; the labeled
// destinations + Integrations/Operations icons + overflow render only when a project is in
// context and the page is not the Support Center (matching today's "no pages dropdown" rule).
// Profile / Manage Users have no projectId, so they are excluded the same way.
export const NavBarRedesign: FunctionComponent<Props> = (props) => {
  const {
    userIsLoggedIn,
    projectId,
    organizationId,
    floorId,
    scanDatasetId,
    pageName,
    pageTitle,
    userRole,
  } = props;

  const { destinations, icons, overflow } = useRedesignNavGroups(projectId, userRole);

  const isSuperadmin = userRole === UserRole.SUPERADMIN;
  const logoLink = isSuperadmin ? toSuperadminOrganizations() : toProjectPortfolioPage();
  const logoTooltip = isSuperadmin ? "Go to Organizations" : "Go to your project portfolio";

  if (!userIsLoggedIn) {
    return <Paper className="NavBar NavBarRedesign hexagon-construction-analysis-logo" square>
      <span className="NavBar-logo">{LogoIcon}</span>
    </Paper>;
  }

  const routeContext = { organizationId, projectId, floorId, scanDatasetId };
  const showDestinations = !!projectId && pageName !== PageNames.SUPPORT_CENTER;

  // The Figma "Selected=Manage users" variant uses wider (16px) side padding than the project
  // pages (12px); the modifier carries that difference.
  const className = classNames("NavBar NavBarRedesign hexagon-construction-analysis-logo", {
    "NavBarRedesign--manageUsers": pageName === PageNames.MANAGE_USERS,
  });

  return <Paper className={className} square elevation={0}>
    <div className="NavBarRedesign-left">
      <span className="NavBarRedesign-analyze">
        <Tooltipable title={logoTooltip}>
          <Link className="NavBar-logo" to={logoLink} aria-label="Hexagon Logo">
            {LogoIcon}
          </Link>
        </Tooltipable>
        <span className="NavBarRedesign-logoDivider" aria-hidden="true"/>
      </span>
      <span className="NavBar-pageName" aria-label={projectId ? "Project Name" : "Page Title"}>{pageTitle}</span>
    </div>
    {showDestinations && <NavBarRedesignDestinations destinations={destinations} pageName={pageName} routeContext={routeContext}/>}
    <NavBarRedesignActions
      icons={showDestinations ? icons : []}
      overflow={showDestinations ? overflow : []}
      pageName={pageName}
      routeContext={routeContext}
    />
  </Paper>;
};

const mapStateToProps = (state, props) => ({
  userIsLoggedIn: getUserIsLoggedIn(state, props),
  projectId: getProjectId(state, props),
  organizationId: getOrganizationId(state, props),
  floorId: getFloorIdFromLocation(state, props),
  scanDatasetId: getScanDatasetIdFromLocation(state, props),
  pageName: getPageName(state, props),
  pageTitle: getRedesignPageTitle(state, props),
  userRole: getUserRole(state, props),
});

export default connect(mapStateToProps)(NavBarRedesign);
